import { useCallback, useInsertionEffect, useRef } from 'react'

/**
 * Polyfill of React's experimental `useEffectEvent`: returns a callback with a
 * stable identity that always invokes the latest `fn`. Lets effects and
 * handlers read fresh props/state without re-subscribing or re-creating
 * callbacks. Pure — no DOM.
 *
 * Ref: https://stackoverflow.com/questions/76335194
 */
export function useEffectEvent<Args extends unknown[], Return>(
  fn: (...args: Args) => Return,
): (...args: Args) => Return {
  const savedCallback = useRef(fn)

  // Keep the latest fn without changing the returned callback's identity.
  useInsertionEffect(() => {
    savedCallback.current = fn
  }, [fn])

  return useCallback((...args: Args) => savedCallback.current(...args), [])
}
